import { useConfig } from '@exam-management/configuration';
import { Navigate, type Path } from '@exam-management/router';
import { AuthenticationState, useAuth } from '@shared/auth';
import { Loading } from '@shared/components';
import { usePermission } from '@shared/permissions';
import { type PropsWithChildren } from '@shared/utils';
import { useLocation, matchPath } from 'react-router-dom';

const USER_NOT_ACTIVE_PATHS: Path[] = ['/user-not-active'] as const;

type ProtectedRouteProps = PropsWithChildren;

export default function ProtectedRoute(props: Readonly<ProtectedRouteProps>) {
  const { children } = props;

  const {
    authenticationState: authState,
    userProfile,
    profileLoading: isProfileLoading,
  } = useAuth();
  const { loading: isPermissionLoading } = usePermission();
  const location = useLocation();
  const { loginUrl } = useConfig();

  const isLoading =
    authState === AuthenticationState.LOADING || isProfileLoading || isPermissionLoading;
  const isLoggedIn = authState === AuthenticationState.LOGGED_IN && !!userProfile;
  const isActiveUser = userProfile?.status === 'ENABLED';

  const isNotActivePath = USER_NOT_ACTIVE_PATHS.some((path) => matchPath(path, location.pathname));

  if (isLoading) {
    return <Loading sx={{ height: '100dvh' }} />;
  }

  // CASE: Not Logged in
  if (!isLoggedIn) {
    const redirectTo = window.location.href;
    window.location.href = redirectTo
      ? `${loginUrl}?redirectTo=${encodeURIComponent(redirectTo)}`
      : loginUrl;
    return; // NOTE: use to prevent navigating to other cases below
  }

  // CASE: Logged in
  if (!isActiveUser && !isNotActivePath) {
    return <Navigate to='/user-not-active' replace />;
  }

  return children;
}
